export default {
    methods: {
        /**
         * Resolve a field type id to its base type family.
         * Demographic SELECT variants (SELECT_GENDER, SELECT_ETHNICITY, SELECT_IWI, ...)
         * behave as a SELECT; DATETIME behaves as a DATE with an added time portion.
         * Any other type maps to itself.
         * @param fieldType the field type id from form_field_types
         * @return base type string used by switch/render logic
         */
        getBaseFieldType(fieldType) {
            if (typeof fieldType !== 'string') return fieldType;
            if (fieldType.startsWith('SELECT_')) return 'SELECT';
            if (fieldType === 'DATETIME') return 'DATE';
            return fieldType;
        },
        resolveFieldComponentByType(fieldType, options = {}) {
            const { includeMulti = false } = options;
            const componentsByType = {
                STAR_RATING: 'StarRatingField',
                SCALE: 'ScaleField',
                TEXT: 'TextField',
                DURATION: 'DurationField',
                STAFF_NAME: 'StaffNameField',
                RADIO: 'RadioField',
                SELECT: 'SelectField',
                DATE: 'DateField',
                CONTACT: 'CellsField',
                CELLS: 'CellsField',
                CHECKLIST: 'ChecklistField',
            };

            if (includeMulti) {
                componentsByType.MULTI = 'MultiField';
            }

            return componentsByType[this.getBaseFieldType(fieldType)] || null;
        },
        buildDateValidationRules(fieldOptions = {}, options = {}) {
            const { hasTime = false } = options;
            return [
                (value) => {
                    if (!value) return true;

                    // DATETIME values arrive as "DD/MM/YYYY HH:mm" — split the date from the time
                    const [datePart, timePart] = value.split(' ');
                    const parts = datePart.split('/');
                    if (parts.length !== 3) {
                        return hasTime ? 'Must be in DD/MM/YYYY HH:mm format' : 'Must be in DD/MM/YYYY format';
                    }

                    if (hasTime) {
                        if (!timePart || !/^\d{1,2}:\d{2}$/.test(timePart)) {
                            return 'Must be in DD/MM/YYYY HH:mm format';
                        }
                        const [hourNum, minuteNum] = timePart.split(':').map(n => parseInt(n, 10));
                        if (hourNum < 0 || hourNum > 23 || minuteNum < 0 || minuteNum > 59) {
                            return 'Must be a valid time';
                        }
                    }

                    const [day, month, year] = parts;
                    const dayNum = parseInt(day, 10);
                    const monthNum = parseInt(month, 10);
                    const yearNum = parseInt(year, 10);

                    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) {
                        return 'Must be a valid date';
                    }

                    if (monthNum < 1 || monthNum > 12) {
                        return 'Must be a valid date';
                    }

                    const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
                    if (dayNum < 1 || dayNum > daysInMonth) {
                        return 'Must be a valid date';
                    }

                    const dateValue = new Date(yearNum, monthNum - 1, dayNum);
                    if (isNaN(dateValue.getTime())) {
                        return 'Must be a valid date';
                    }

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    dateValue.setHours(0, 0, 0, 0);

                    const allowFuture = fieldOptions?.allowFuture !== false;
                    const allowPast = fieldOptions?.allowPast !== false;

                    if (dateValue > today && !allowFuture) {
                        return 'Cannot be a future date';
                    }

                    if (dateValue < today && !allowPast) {
                        return 'Cannot be a past date';
                    }

                    if (fieldOptions?.minDate) {
                        const minParts = fieldOptions.minDate.split('/');
                        const minDate = new Date(minParts[2], minParts[1] - 1, minParts[0]);
                        minDate.setHours(0, 0, 0, 0);
                        if (dateValue < minDate) {
                            return `Must be on or after ${fieldOptions.minDate}`;
                        }
                    }

                    if (fieldOptions?.maxDate) {
                        const maxParts = fieldOptions.maxDate.split('/');
                        const maxDate = new Date(maxParts[2], maxParts[1] - 1, maxParts[0]);
                        maxDate.setHours(0, 0, 0, 0);
                        if (dateValue > maxDate) {
                            return `Must be on or before ${fieldOptions.maxDate}`;
                        }
                    }

                    return true;
                },
            ];
        },
        buildDatePickerOptions(fieldOptions = {}) {
            return (date) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const checkDate = new Date(date);

                if ((fieldOptions?.allowFuture === false) && (checkDate > today)) {
                    return false;
                }
                if ((fieldOptions?.allowPast === false) && (checkDate < today)) {
                    return false;
                }
                if (fieldOptions?.minDate) {
                    const minDate = new Date(fieldOptions.minDate.split('/').reverse().join('-'));
                    if (checkDate < minDate) {
                        return false;
                    }
                }
                if (fieldOptions?.maxDate) {
                    const maxDate = new Date(fieldOptions.maxDate.split('/').reverse().join('-'));
                    if (checkDate > maxDate) {
                        return false;
                    }
                }
                return true;
            };
        },
    },
};
